import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, User, Phone, CreditCard } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import AuthLayout from '../../components/auth/AuthLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import MaskedInput from '../../components/common/MaskedInput';
import Select from '../../components/common/Select';

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    cpf: '',
    type: 'reviter',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }
    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    setLoading(true);
    try {
      const { confirmPassword, ...userData } = formData;
      const result = await signup(userData);
      if (result.success) {
        navigate('/catalogo');
      } else {
        setError(result.error || 'Não foi possível criar a conta.');
      }
    } catch {
      setError('Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Criar conta"
      subtitle="Cadastre-se em menos de um minuto e comece a vender ou comprar."
      footer={
        <p className="text-app-text-secondary">
          Já tem uma conta?{' '}
          <Link to="/entrar" className="text-primary hover:underline font-semibold">
            Entrar
          </Link>
        </p>
      }
    >
      {error && (
        <div className="bg-error/10 border border-error/30 text-error px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Tipo de conta"
          name="type"
          value={formData.type}
          onChange={handleChange}
          options={[
            { value: 'reviter', label: 'Revendedor' },
            { value: 'client', label: 'Cliente' },
          ]}
        />
        <Input
          label="Nome completo"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="Seu nome"
          leftIcon={<User size={18} />}
        />
        <Input
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
          placeholder="seu@email.com"
          leftIcon={<Mail size={18} />}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <MaskedInput
            label="Telefone"
            name="phone"
            mask="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(11) 98765-4321"
            leftIcon={<Phone size={18} />}
          />
          <MaskedInput
            label="CPF (opcional)"
            name="cpf"
            mask="cpf"
            value={formData.cpf}
            onChange={handleChange}
            placeholder="123.456.789-00"
            leftIcon={<CreditCard size={18} />}
          />
        </div>
        <Input
          label="Senha"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange}
          required
          placeholder="••••••••"
          helpText="Mínimo 6 caracteres"
          leftIcon={<Lock size={18} />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="hover:text-app-text transition-colors pointer-events-auto"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
        />
        <Input
          label="Confirmar senha"
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          value={formData.confirmPassword}
          onChange={handleChange}
          required
          placeholder="••••••••"
          leftIcon={<Lock size={18} />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              className="hover:text-app-text transition-colors pointer-events-auto"
              aria-label={showConfirmPassword ? 'Ocultar' : 'Mostrar'}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
        />
        <Button type="submit" fullWidth loading={loading} size="lg">
          Criar conta
        </Button>
      </form>
    </AuthLayout>
  );
};

export default SignupPage;
